import { type ElementType, type PropsWithChildren, forwardRef } from "react";
import Classes from "./DDSHeadlineText.module.css";

export type DDSHeadlineTextProps = {
  id?: string;
  as?: "span" | "h1" | "h2" | "h3" | "h4" | "h5";
  /**
   * Headline size — matches the `--headline-{size}-*` CSS tokens.
   *
   * Renamed in 2026 from numeric levels to t-shirt strings: `1`→`"lg"`, `2`→`"md"`,
   * `3`→`"sm"`. See the [type system migration spec](https://linear.app/classdojo/document/type-system-migration-spec-and-codefigma-differences-a635b955ad0a)
   * for the full mapping (Figma may still display legacy "Headline/1" style names on stale comps).
   */
  level?: "lg" | "md" | "sm";
  className?: string;
  "aria-label"?: string;
  "data-name"?: string;
  role?: string;
};

const LEVEL_TO_CLASS = {
  lg: "headlineLg",
  md: "headlineMd",
  sm: "headlineSm",
} as const;

export const DDSHeadlineText = forwardRef<HTMLElement, PropsWithChildren<DDSHeadlineTextProps>>(
  (
    { as: tagName = "h2", level = "lg", className, id, children, "aria-label": ariaLabel, "data-name": dataName, role },
    ref,
  ) => {
    const Tag = tagName as ElementType;
    return (
      <Tag
        ref={ref}
        id={id}
        className={`${className ?? ""} ${Classes[LEVEL_TO_CLASS[level]]}`}
        aria-label={ariaLabel}
        data-name={dataName}
        role={role}
      >
        {children}
      </Tag>
    );
  },
);

DDSHeadlineText.displayName = "DDSHeadlineText";
